import React from 'react'
import { useSelector } from 'react-redux'
import { useTrack } from 'src/utils/useTrack'
import { useWatchPing } from 'src/utils/useWatchPing'

/**
 * Headless watch-time tracker for the auditorium — renders nothing, owns both
 * tracking systems.
 *
 * MUST be mounted from App.tsx OUTSIDE the <Media> desktop/mobile split. The
 * auditorium is routed twice, once inside each branch of that split, so
 * crossing the 819px breakpoint swaps one PrivateRoute subtree for the other
 * and REMOUNTS AuditoriumContainer. While the hooks lived in that container
 * every breakpoint cross tore down useTrack — firing an exit beacon and
 * starting a fresh span mid-watch.
 *
 * That is not a rare edge case: a phone entering video fullscreen rotates to
 * landscape, and most modern handsets are wider than 819px in landscape, so a
 * single viewer could fragment one continuous session into many — each
 * fragment depending on a teardown beacon that mobile browsers routinely drop.
 *
 * Mounted from a plain <Route> at App level, the hooks now mount once on
 * entering /auditorium-hall and unmount once on leaving it. Rotation,
 * fullscreen and browser resize are invisible to them.
 */

interface AuthSlice {
  auth: {
    appReady: boolean
    isAuthenticated: boolean
  }
}

// Hooks cannot be called conditionally, so the auth gate lives in the parent
// and the hooks live in a child that is mounted or unmounted as a whole.
const TrackingHooks = () => {
  useTrack({})
  // System B: the independent append-only watch-time backup. It shares no code
  // path with useTrack above — deleting this one line disables B completely.
  useWatchPing({})
  return null
}

export default function AuditoriumTracker() {
  const { appReady, isAuthenticated } = useSelector((state: AuthSlice) => state.auth)

  // Mirror PrivateRoute's gate: while it renders a <Loader/> there is no
  // auditorium to track. This also keeps useTrack from heartbeating without a
  // JWT and tripping its three-strike 401 reload.
  if (!appReady || !isAuthenticated) return null

  return <TrackingHooks />
}
